import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useEstadoPreservado } from '@/lib/estadoDeSessao';
import { equipeIncoerente, pedirRenovacao } from '@/lib/copiaLocal';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { ouvirEquipe } from '@/services/usuariosService';
import { ouvirClinica } from '@/services/clinicasService';
import {
  buscarConversaPorId,
  buscarConversas,
  ouvirConversas,
  ouvirEtiquetas,
  ouvirNumeros,
  ouvirRespostasRapidas,
} from '@/services/conversasService';
import { FILTROS_VAZIOS, aplicarFiltros, contarAtalhos } from '@/conversas/filtros';
import { canalPadrao, canalPorId, filtrarPorCanal, papelPodeNoCanal } from '@/conversas/canais';
import { registrarErro, tratarErro } from '@/lib/erros';

const ConversasContext = createContext(null);

/**
 * ★★★ WV — O TEMPO LIMITE DO RESGATE, e por que ele é 10 s.
 *
 * ⚠⚠ A medição de 08/out (`docs/medicao-abrindo-conversa.md`) achou um `getDoc` de **um
 * documento por id** esperando sem prazo nenhum. ★ Esse `getDoc` leva dezenas de
 * milissegundos no caminho normal; **dez segundos não é "devagar", é "não vem"**.
 *
 * ★★ Ele também fecha a hipótese que a medição **não conseguiu descartar** — rede lenta —
 * sem precisar distingui-la do cancelamento: ⚠ nos dois casos a pessoa estava presa em
 * *"Abrindo a conversa…"* **sem saída**, e agora os dois terminam numa tela com botão.
 */
export const LIMITE_RESGATE_MS = 10_000;

/**
 * ★ A frase do tempo limite. Ela é a chave que a tela usa para mostrar o "Tentar de novo" —
 * ⚠ comparar por texto é frágil, mas a constante é exportada e os testes leem daqui, não de
 * uma cópia digitada.
 */
export const AVISO_RESGATE_DEMOROU = 'Não conseguimos abrir esta conversa.';

export function ConversasProvider() {
  const { clinicaId, usuario, perfil, papel } = useAuth();

  // O canal vem da ROTA, não de um estado local: é o menu que escolhe agora, e
  // a URL passa a ser compartilhável e recarregável.
  const { canal: canalId } = useParams();
  const canal = canalPorId(canalId);
  const permitido = canal ? papelPodeNoCanal(canal, papel) : false;

  // Só assina o que o papel pode ler. Sem isto, um acesso direto a
  // /conversas/recepcao por um SDR dispararia a query e voltaria
  // permission-denied das rules — erro no console para algo que a rota já sabe
  // barrar.
  const espaco = permitido ? canal.espaco : null;

  const [conversas, setConversas] = useState([]);
  const [equipe, setEquipe] = useState([]);
  const [etiquetas, setEtiquetas] = useState([]);
  const [respostas, setRespostas] = useState([]);
  const [numeros, setNumeros] = useState([]);
  // Config da clínica (cadência de follow-up e procedimentos): o painel do
  // contato passou a trabalhar o lead sem sair da conversa (V2), e essas duas
  // listas vinham só pelo LeadsProvider, que esta rota não tem.
  const [clinica, setClinica] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  /**
   * Conversas achadas NO BANCO pela busca, fora das 200 assinadas.
   *
   * Vivem separadas de `conversas` de propósito: aquela lista é o retrato ao
   * vivo do inbox e é reescrita a cada snapshot. Misturar os achados ali faria
   * o próximo snapshot apagá-los, e a conversa encontrada sumiria sozinha da
   * tela um segundo depois.
   */
  const [achadas, setAchadas] = useState([]);
  /** ★ WT 1b: a busca devolveu menos do que existe (uma das consultas foi recusada). */
  const [buscaIncompleta, setBuscaIncompleta] = useState(false);
  /**
   * ★★★ WS — a conversa RESGATADA POR ID, quando a seleção aponta para fora das 200.
   *
   * Vive separada de `conversas` pelo mesmo motivo que `achadas`: aquela lista é o retrato
   * ao vivo e é reescrita a cada snapshot, então um resgate guardado ali sumiria da tela no
   * snapshot seguinte.
   */
  const [resgatada, setResgatada] = useState(null);
  /** Aviso quando a seleção aponta para uma conversa que não é deste canal (ou não existe). */
  const [avisoSelecao, setAvisoSelecao] = useState(null);
  /**
   * ★★★ WT lote 1b — O INTERVALO EM QUE A CONVERSA ESTÁ SENDO BUSCADA.
   *
   * ⚠⚠ Relato do dono (06/out, lead "Dalva"): clicou, foi para `/conversas/resgate` e viu
   * *"Escolha uma conversa à esquerda"*. ★ Medido: a conversa é a **231ª de 1.147** — fora
   * das 200 que o inbox assina — e o resgate por id **estava em voo**.
   *
   * ★★★ Entre a chegada das 200 e a resposta do `getDoc`, `conversa` e `avisoSelecao` eram
   * `null`, e a tela caía na frase padrão. ⚠ **"Escolha uma conversa" para quem acabou de
   * escolher uma é a tela chamando a pessoa de distraída** — e foi o que a fez relatar como
   * defeito algo que ia funcionar um segundo depois.
   */
  const [abrindoSelecao, setAbrindoSelecao] = useState(false);
  /**
   * ★★ WV — O CONTADOR DO "Tentar de novo".
   *
   * ⚠ `refTentados` é uma `ref`: apagar a chave dela **não reexecuta o efeito**. ★ Este
   * contador é o estado que dispara a execução nova — ele não é lido por ninguém, só
   * mudado; é um botão de rearme, não um dado.
   */
  const [tentativaResgate, setTentativaResgate] = useState(0);
  /**
   * ★★★ WT lote 1 — O QUE NÃO CARREGOU, por assunto.
   *
   * ⚠⚠ Os cinco listeners abaixo tinham `() => {}` como tratador: equipe, etiquetas,
   * respostas rápidas, números e clínica falhavam **em silêncio absoluto**
   * (`docs/wt-medicao.md` §1.2). ★ O da EQUIPE é o pior: foi ele que produziu o
   * *"Jessica Paterno (fora da função)"* de 03/out — a lista chegava incompleta e a tela
   * acusava o cadastro de uma pessoa.
   *
   * ★ Agora cada um marca o seu assunto, e a tela diz que não carregou.
   */
  const [falhas, setFalhas] = useState({});
  const marcarFalha = (assunto) => () => setFalhas((f) => (f[assunto] ? f : { ...f, [assunto]: true }));
  /**
   * ★★ A GUARDA DE LOOP: **um id, uma leitura por execução do efeito**.
   *
   * ⚠ Sem isto, uma conversa fora das 200 poderia disparar uma leitura por reexecução, e
   * cada leitura é dinheiro. ★ `useRef` e não `useState` de propósito: anotar a tentativa
   * NÃO pode re-renderizar, senão o próprio registro realimenta o efeito.
   *
   * ⚠⚠ **WV mudou o significado de "definitivo" aqui.** Antes a chave ficava para sempre, e
   * um cancelamento virava perda permanente — a reexecução batia nela e saía sem tentar nem
   * apagar *"Abrindo a conversa…"*. ★ Agora a chave é solta em dois momentos: no **cancelamento
   * sem resposta** (limpeza do efeito) e no **tempo limite**. ★★ A proteção continua: enquanto
   * uma tentativa está em voo ou já respondeu, a chave está lá e nada relê.
   */
  const refTentados = useRef(new Set());
  // Preservada POR CANAL. Sair do inbox e voltar reabria a lista sem conversa
  // nenhuma; a chave com o canal é o que mantém a regra de baixo — trocar de
  // canal não arrasta a conversa aberta para uma fila onde ela não aparece.
  const [selecionada, setSelecionada] = useEstadoPreservado(
    `inbox:selecionada:${canalId ?? 'padrao'}`,
    null,
  );

  // Leads e Resgate dividem o espaço 'vendas', então trocar entre eles não
  // reexecuta o efeito de assinatura abaixo — e a conversa aberta continuaria
  // selecionada num canal onde ela não aparece. Isso agora é responsabilidade
  // da CHAVE do `useEstadoPreservado` acima: ela inclui o canal, então trocar
  // de canal já troca o valor. Um `setSelecionada(null)` aqui apagaria também
  // a conversa restaurada ao voltar de outra tela.

  /**
   * ★★★ WU/U3 — O SINAL DA JÉSSICA: a equipe chegou e o PRÓPRIO usuário não está nela.
   *
   * ⚠⚠ Isso **não tem explicação legítima**: quem está logado sempre está na própria equipe
   * (`usuarios` com `ativo: true`). ★★ Quando acontece, a lista veio de um retrato velho da
   * cópia local — e foi exatamente o que produziu *"Jessica Paterno (fora da função)"* por cinco
   * dias, em 29/set–03/out.
   *
   * ★ `pedirRenovacao` só MARCA (a limpeza exige a próxima carga) e respeita a trava de
   * **uma renovação por hora**; na segunda vez ele registra o erro e para de oferecer.
   */
  const receberEquipe = (lista) => {
    setEquipe(lista);
    if (equipeIncoerente(lista, usuario?.uid)) pedirRenovacao(usuario.uid);
  };

  useEffect(() => {
    if (!clinicaId || !espaco) return undefined;
    setCarregando(true);
    // NÃO limpa a seleção aqui. Este efeito roda em toda MONTAGEM, não só
    // quando o espaço muda — e o que ele apagava era justamente a conversa
    // restaurada ao voltar de outra tela. Trocar de canal continua limpando,
    // porque a chave do `useEstadoPreservado` acima inclui o canal; trocar de
    // clínica também, porque a troca de usuário esvazia o store inteiro.
    return ouvirConversas(
      clinicaId,
      espaco,
      (lista) => {
        setConversas(lista);
        setErro(null);
        setCarregando(false);
      },
      (e) => {
        console.error('[OdontoSDR] Falha ao ouvir conversas:', e);
        setErro(
          e.code === 'permission-denied'
            ? 'Seu papel não tem acesso a este espaço de atendimento.'
            : 'Não foi possível carregar as conversas.',
        );
        setCarregando(false);
      },
    );
  }, [clinicaId, espaco]);

  /**
   * Procura no banco enquanto a pessoa digita — com atraso, e cancelável.
   *
   * O atraso de 350 ms não é enfeite: sem ele cada tecla dispara três queries.
   * O `cancelado` evita a corrida clássica — a resposta de "62 9" chegando
   * depois da de "62 99664", e sobrescrevendo o resultado certo pelo mais amplo.
   */
  useEffect(() => {
    const termo = filtros.busca.trim();
    if (!clinicaId || termo.length < 3) {
      setAchadas([]);
      setBuscaIncompleta(false);
      return undefined;
    }

    let cancelado = false;
    const timer = setTimeout(async () => {
      const lista = await buscarConversas(clinicaId, espaco, termo);
      // ★★ WT 1b: `lista.incompleta` vem do serviço quando UMA das consultas da busca foi
      // recusada. ⚠ Sem ler a marca, a tela mostrava menos resultados como se fossem todos.
      if (!cancelado) { setAchadas(lista); setBuscaIncompleta(Boolean(lista?.incompleta)); }
    }, 350);

    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
  }, [clinicaId, espaco, filtros.busca]);

  /**
   * ★★★ WS — RESGATA A CONVERSA SELECIONADA QUANDO ELA NÃO ESTÁ NAS 200.
   *
   * ⚠⚠ O defeito que isto conserta: `ouvirConversas` assina as **200** mais recentes por
   * `atualizadoEm`, e a seleção só valia dentro dessa lista. A ficha do lead grava a seleção e
   * navega — mas um lead sem mensagem nos últimos dias tem a conversa fora do corte.
   * ★ Medido: **127 dos 220** leads com tarefa aberta (`docs/medicao-conversa-ficha.md`).
   *
   * ★★ POR QUE NÃO AUMENTAR O `limit`: 1.363 conversas de vendas hoje, e crescendo. Subir para
   * 2.000 troca um defeito certo por um defeito adiado, e paga 2.000 leituras a cada abertura.
   * ★ Aqui é **uma** leitura, e só quando a seleção realmente não está na lista.
   *
   * ★ `carregando` no `if` evita a leitura inútil: durante a primeira carga `conversas` está
   * vazia e TODA seleção pareceria estar fora das 200.
   *
   * ## ⚠⚠⚠ WV (08/out/2026) — ELE TRAVAVA EM "Abrindo a conversa…", E POR TRÊS MOTIVOS
   *
   * ★ Medido e **reproduzido** (`docs/medicao-abrindo-conversa.md`): o lead **Rafa**, conversa na
   * **posição 462 de 1.363** em vendas. O dono clicou, a tela ficou "um tempão" em
   * *"Abrindo a conversa…"* e **não abriu**; o segundo clique abriu.
   *
   * ⚠⚠ **(1) `conversas` estava nas dependências** — e é um **array novo a cada emissão do
   * `onSnapshot`**. ★★★ Uma mensagem chegando em qualquer das 200 **cancelava o resgate em voo**,
   * o `.then` saía no `if (cancelado)` e **nunca apagava o aviso**.
   * ⚠⚠ **(2) `refTentados` era definitivo** — a reexecução batia nele e **não tentava de novo**.
   * ⚠⚠ **(3) não havia tempo limite** — *"Abrindo a conversa…"* **não tinha saída em caso nenhum**.
   *
   * ★★ Experimento controlado: com `conversas` nas dependências, trava; **tirando-o, não trava**.
   * ★★★ **Qualquer um dos três, sozinho, seria inofensivo.** Os três consertos estão abaixo.
   *
   * ★ Serve os TRÊS canais (`leads` e `resgate` em vendas, `recepcao`): o `espaco` vem da rota.
   * ⚠ Na recepção são **821** conversas fora das 200 — o mesmo tamanho de problema.
   */

  /**
   * ★★★ WV (1) — O BOOLEANO, não o array.
   *
   * ⚠⚠ `conversas.some(...)` dentro do efeito obrigava `conversas` a ser dependência. ★ Aqui o
   * `useMemo` devolve **true/false**, que só muda quando **a resposta** muda — não a cada
   * emissão do listener. **É o conserto do cancelamento à toa.**
   */
  const estaNaLista = useMemo(
    () => Boolean(selecionada) && conversas.some((c) => c.id === selecionada),
    [conversas, selecionada],
  );

  useEffect(() => {
    if (!clinicaId || !espaco || !selecionada || carregando) return undefined;
    if (estaNaLista) {
      // Está na lista assinada? Então não há nada a resgatar — e o caso comum não lê nada.
      setResgatada(null);
      setAvisoSelecao(null);
      setAbrindoSelecao(false);
      return undefined;
    }
    // ⚠ A chave inclui clínica e espaço: a MESMA conversa pode ser legítima num canal e
    // estranha no outro, e trocar de clínica tem de permitir uma leitura nova.
    const chave = `${clinicaId}:${espaco}:${selecionada}`;
    if (refTentados.current.has(chave)) return undefined;
    refTentados.current.add(chave);

    let cancelado = false;
    let respondeu = false;
    // ★★ WT 1b: a tela passa a DIZER que está abrindo, em vez de mandar escolher outra.
    setAbrindoSelecao(true);
    setAvisoSelecao(null);

    /**
     * ★★★ WV (3) — O TEMPO LIMITE. Antes, nenhum ponto de espera deste caminho tinha um.
     *
     * ⚠⚠ 10 s: o `getDoc` de um documento por id leva dezenas de milissegundos; ★ dez segundos
     * é "algo está errado", não "a rede está devagar". ★★ E ele também cobre a hipótese que eu
     * **não consegui descartar** na medição — rede lenta —, sem precisar distingui-la:
     * **nos dois casos a pessoa sai do limbo.**
     */
    const relogio = setTimeout(() => {
      if (cancelado || respondeu) return;
      respondeu = true;
      refTentados.current.delete(chave);
      setAbrindoSelecao(false);
      setResgatada(null);
      setAvisoSelecao(AVISO_RESGATE_DEMOROU);
      /**
       * ★★★ O REGISTRO — e o id da conversa vai na MENSAGEM, de propósito.
       *
       * ⚠⚠ O id de uma conversa é `resgate__+5562994579531`: **ele contém o telefone**.
       * `codigo` não passa por `limparMensagem` e gravaria o número cru; ★ `mensagem` passa,
       * e a corrida de 8+ dígitos vira `[numero]` — sobra `resgate__+[numero]`, que ainda diz
       * o CANAL e que é conversa de JID. ⚠ Conversa com id automático do Firestore
       * (`8cvbpXXdbuxZkTWYBWEY`) atravessa inteira, que é o caso em que o id é mais útil.
       * ★★ Então sim, o id vai — com a parte que é dado de paciente mascarada pelo filtro que
       * já existe. **Não é o id inteiro no banco, e isso é a regra de LGPD, não um descuido.**
       * ★ O inteiro vai em `extra`, que o `erros.js` manda só para o console local.
       *
       * ★★ E o `codigo`/`mensagem` NÃO dizem "resgate": ⚠ os dois aparecem na tela de Saúde
       * (`.lista-erros`), e a decisão do dono de 19/set tirou a palavra "resgate" do texto de
       * tela — o guarda da VT pegou isto, e ele estava certo. ★ *"Não abriu a conversa"* é o
       * que a pessoa viu acontecer; *"resgate"* é o nome do mecanismo, e só serve aqui dentro.
       */
      registrarErro({
        origem: 'service',
        codigo: 'abrir-conversa:tempo-limite',
        mensagem: `Não abriu a conversa ${selecionada} em ${LIMITE_RESGATE_MS / 1000}s`,
        extra: { conversaId: selecionada, espaco },
      });
    }, LIMITE_RESGATE_MS);

    buscarConversaPorId(clinicaId, selecionada)
      .then((c) => {
        if (cancelado || respondeu) return;
        respondeu = true;
        clearTimeout(relogio);
        /**
         * ★★ A CONFERÊNCIA É AQUI, não no serviço — e ela não é segurança, é coerência.
         * ⚠ A autoridade continua sendo as rules; o que se confere aqui é se o documento
         * pertence a ESTE canal.
         */
        setAbrindoSelecao(false);
        if (!c || c.clinicaId !== clinicaId || c.espaco !== espaco) {
          setResgatada(null);
          setAvisoSelecao('A conversa deste lead não foi encontrada neste canal.');
          return;
        }
        setResgatada(c);
        setAvisoSelecao(null);
      })
      .catch(tratarErro('a conversa selecionada', () => {
        if (cancelado || respondeu) return;
        respondeu = true;
        clearTimeout(relogio);
        setAbrindoSelecao(false);
        setResgatada(null);
        setAvisoSelecao('A conversa deste lead não foi encontrada neste canal.');
      }, 'service'));

    return () => {
      cancelado = true;
      clearTimeout(relogio);
      /**
       * ★★★ WV (2) — A CHAVE É SOLTA NO CANCELAMENTO.
       *
       * ⚠⚠ Antes ela ficava para sempre, e um cancelamento virava **perda permanente**: a
       * reexecução batia em `refTentados` e saía sem tentar nem apagar o aviso.
       * ★ Soltando só quando **ainda não respondeu**, a proteção contra laço continua (uma
       * tentativa por execução) — ⚠ mas um cancelamento passa a ser **retentável**.
       */
      if (!respondeu) refTentados.current.delete(chave);
    };
  }, [clinicaId, espaco, selecionada, carregando, estaNaLista, tentativaResgate]);

  /**
   * ★★ WV (3) — "Tentar de novo": solta a chave e força uma execução nova do efeito.
   *
   * ⚠⚠ Mexer SÓ no `refTentados` não reexecutaria o efeito — ele é uma `ref`, e mudar `ref`
   * não re-renderiza nada. ★ Por isso o botão faz as **duas** coisas: solta a chave E
   * incrementa `tentativaResgate`, que está nas dependências do efeito.
   * ★★★ É aqui que mora a mutação (d): um botão que só limpasse o aviso pareceria funcionar
   * (a frase sai da tela) e **não buscaria nada** — a pessoa voltaria ao limbo.
   */
  const tentarResgateDeNovo = useCallback(() => {
    if (!clinicaId || !espaco || !selecionada) return;
    refTentados.current.delete(`${clinicaId}:${espaco}:${selecionada}`);
    setAvisoSelecao(null);
    setTentativaResgate((n) => n + 1);
  }, [clinicaId, espaco, selecionada]);

  useEffect(() => {
    if (!clinicaId) return undefined;
    // ★ WT: cada listener avisa o SEU assunto. ⚠ O registro do erro já acontece dentro do
    // serviço (`tratarErro`), então nem o `() => {}` escaparia dele — mas a TELA só sabe
    // porque estes tratadores existem.
    const cancelamentos = [
      ouvirEquipe(clinicaId, receberEquipe, marcarFalha('a equipe')),
      ouvirEtiquetas(clinicaId, setEtiquetas, marcarFalha('as etiquetas')),
      ouvirRespostasRapidas(clinicaId, setRespostas, marcarFalha('as respostas rápidas')),
      ouvirNumeros(clinicaId, setNumeros, marcarFalha('os números')),
      ouvirClinica(clinicaId, setClinica, marcarFalha('a configuração da clínica')),
    ];
    return () => cancelamentos.forEach((cancelar) => cancelar());
  }, [clinicaId]);

  const valor = useMemo(() => {
    // O recorte do canal vem ANTES de tudo: as abas e os contadores da lista
    // precisam contar só o canal aberto, senão o número no topo não bate com o
    // que está embaixo dele.
    /**
     * A lista ao vivo MAIS o que a busca achou no banco, deduplicado por id.
     *
     * As assinadas vêm primeiro no `Map`, então uma conversa que está nos dois
     * lados fica com a versão AO VIVO — a da busca é um retrato de um instante e
     * ficaria desatualizada (não lida, último texto) se ganhasse a disputa.
     *
     * Sem busca ativa, `achadas` é vazio e isto é exatamente a lista de antes.
     */
    /**
     * ★★ WS — a RESGATADA entra PRIMEIRO, e isso é a ordenação dela.
     *
     * ★ `Map.set` numa chave que já existe **troca o valor e mantém a posição**: pondo a
     * resgatada antes e as assinadas depois, ela fica no TOPO da lista e, se também estiver
     * entre as 200, fica com a versão AO VIVO. ⚠ Ela sai do topo quando a pessoa troca de
     * conversa — o efeito acima zera `resgatada` quando a seleção está na lista.
     */
    const unidas = new Map();
    if (resgatada) unidas.set(resgatada.id, resgatada);
    for (const c of conversas) unidas.set(c.id, c);
    for (const c of achadas) if (!unidas.has(c.id)) unidas.set(c.id, c);

    const doCanal = filtrarPorCanal([...unidas.values()], canal);
    /**
     * O `aplicarFiltros` continua rodando por cima, inclusive nos achados: quem
     * veio do banco casou por telefone ou por prefixo de nome, mas ainda precisa
     * respeitar fila, status e etiqueta escolhidos na tela.
     */
    const filtradas = aplicarFiltros(doCanal, filtros, usuario?.uid);
    const atendente = {
      id: usuario?.uid,
      nome: perfil?.nome ?? usuario?.email ?? 'Atendente',
      // ★ WA: o papel anda junto — a reserva libera o gestor, que é quem desempata.
      papel,
    };
    return {
      clinicaId,
      espaco,
      canal,
      conversas: doCanal,
      conversasFiltradas: filtradas,
      // Contado sobre o canal inteiro, não sobre `filtradas`: é o número que
      // aparece ao lado dos atalhos, e ele responde "quanto existe", não
      // "quanto sobrou".
      contagens: contarAtalhos(doCanal, usuario?.uid),
      equipe,
      etiquetas: etiquetas.filter((e) => !e.espaco || e.espaco === espaco || e.espaco === 'ambos'),
      respostas: respostas.filter(
        (r) => !r.espaco || r.espaco === espaco || r.espaco === 'ambos',
      ),
      numeros,
      clinica,
      carregando,
      erro,
      filtros,
      setFiltros,
      limparFiltros: () => setFiltros(FILTROS_VAZIOS),
      conversa: selecionada ? (doCanal.find((c) => c.id === selecionada) ?? null) : null,
      // ★ WS: por que o painel está vazio. Sem isto a tela dizia só "Escolha uma conversa
      // à esquerda" — uma frase que, para quem chegou clicando num lead, é falsa.
      avisoSelecao,
      // ★ WT: os assuntos que NÃO carregaram, para a tela avisar em vez de calar.
      falhas: Object.keys(falhas),
      buscaIncompleta,
      // ★★ WT 1b — a conversa escolhida está sendo buscada (fora das 200).
      abrindoSelecao,
      /**
       * ★★ WV — O BOTÃO. A tela só o mostra quando `avisoSelecao === AVISO_RESGATE_DEMOROU`.
       *
       * ⚠ Ele TENTA DE VERDADE: solta a chave do `refTentados` **e** mexe no estado que
       * reexecuta o efeito. ★ O defeito que isto evita é o botão que limpa o aviso e não
       * busca nada — por isso existe a mutação (d).
       */
      tentarResgateDeNovo,
      selecionar: setSelecionada,
      atendente,
    };
  }, [
    clinicaId, espaco, canal, conversas, achadas, buscaIncompleta, resgatada, avisoSelecao, abrindoSelecao,
    tentarResgateDeNovo, falhas, equipe,
    etiquetas, respostas, numeros, clinica, carregando, erro, filtros, selecionada, usuario, perfil,
  ]);

  // Os desvios ficam DEPOIS de todos os hooks: sair antes quebraria a ordem que
  // o React exige entre renders.

  // Canal desconhecido na URL (ou /conversas sem canal) cai no padrão do papel,
  // em vez de renderizar um inbox vazio sem explicação.
  if (!canal) return <Navigate to={`/conversas/${canalPadrao(papel)}`} replace />;

  // O menu já esconde o que o papel não pode abrir; isto cobre a URL digitada.
  if (!permitido) return <Navigate to="/sem-acesso" replace />;

  return (
    <ConversasContext.Provider value={valor}>
      <Outlet />
    </ConversasContext.Provider>
  );
}

export function useConversas() {
  const ctx = useContext(ConversasContext);
  if (!ctx) throw new Error('useConversas precisa estar dentro de <ConversasProvider>.');
  return ctx;
}
